import React, { useMemo, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Alert, Button } from "reactstrap";
import { useGetReportScheduleRuns } from "api/report-schedules/service";
import { buildUpcomingRunsFromEntries, mergeScheduleRuns } from "./reportScheduleCalendarUtils";
import ScheduleExportsCalendar from "./ScheduleExportsCalendar";
import { getReportSchedulePanelBoxStyle } from "./reportScheduleTheme";

const ReportScheduleHistoryCalendar = ({
  reportId,
  scheduleId,
  runHistory = [],
  nextRunAt = null,
  reportName = "",
  scheduleName = "",
  timezone = "GMT-8",
  colors,
  activeColorStyle,
  onDownloadCsv,
  isDownloadingCsv = false,
  downloadCsvDisabled = false,
  csvDownloadError = "",
  fillHeight = false,
}) => {
  const { t } = useTranslation();
  const [visibleMonth, setVisibleMonth] = useState(() => moment().startOf("month"));
  const year = visibleMonth.year();
  const month = visibleMonth.month() + 1;

  const { data: apiRuns, isLoading } = useGetReportScheduleRuns(
    reportId,
    year,
    month,
    scheduleId
  );

  const runs = useMemo(
    () => mergeScheduleRuns(runHistory, apiRuns || []),
    [runHistory, apiRuns]
  );

  const upcomingRuns = useMemo(
    () =>
      buildUpcomingRunsFromEntries(
        [
          {
            next_run_at: nextRunAt,
            report_id: reportId,
            schedule_id: scheduleId,
            report_name: reportName || (reportId ? `Report #${reportId}` : ""),
            schedule_name: scheduleName,
          },
        ],
        timezone,
        year,
        month
      ),
    [nextRunAt, reportId, scheduleId, reportName, scheduleName, timezone, year, month]
  );

  const downloadSampleButton = onDownloadCsv ? (
    <Button
      color="primary"
      size="sm"
      style={activeColorStyle}
      disabled={downloadCsvDisabled || isDownloadingCsv}
      onClick={onDownloadCsv}
    >
      {isDownloadingCsv ? (
        <>
          <span className="spinner-border spinner-border-sm mr-1" role="status" />
          {t("reports.schedule.downloadingCsv")}
        </>
      ) : (
        <>
          <i className="tim-icons icon-cloud-download-93 mr-1" />
          {t("reports.schedule.downloadCsvSample")}
        </>
      )}
    </Button>
  ) : null;

  const panelStyle = {
    ...getReportSchedulePanelBoxStyle(colors, { marginTop: 0 }),
    ...(fillHeight ? { flex: 1, display: "flex", flexDirection: "column", minHeight: 0 } : {}),
  };

  return (
    <div
      className={`report-schedule-history-wrap d-flex flex-column${
        fillHeight ? " report-schedule-history-wrap--fill" : ""
      }`}
    >
      <div
        className="report-schedule-history-panel report-schedule-panel-box"
        style={panelStyle}
      >
        {csvDownloadError && (
          <Alert color="danger" className="py-2 px-3 mb-2 report-schedule-theme-alert">
            {csvDownloadError}
          </Alert>
        )}
        <ScheduleExportsCalendar
          className="report-schedule-history-calendar"
          title={t("reports.schedule.calendarTitle")}
          headerExtra={downloadSampleButton}
          visibleMonth={visibleMonth}
          onVisibleMonthChange={setVisibleMonth}
          runs={runs}
          upcomingRuns={upcomingRuns}
          isLoading={isLoading}
          timezone={timezone}
          colors={colors}
          activeColorStyle={activeColorStyle}
          fillHeight={fillHeight}
        />
        {onDownloadCsv && (
          <p
            className="mb-0 mt-3 small report-schedule-history-hint"
            style={{ color: colors?.mutedColor || "#6c757d" }}
          >
            {t("reports.schedule.downloadCsvSampleHint")}
          </p>
        )}
      </div>
    </div>
  );
};

export default ReportScheduleHistoryCalendar;
